import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Drawer } from "@relais/design-system";
import { supabase } from "../lib/supabase.js";
import { useOffline } from "../lib/offline-context.js";
import {
  changePassword,
  validatePasswordChange,
  type PasswordValidationError,
} from "@relais/contracts";

// Codes de validation → clés i18n. "empty" ne devrait pas être atteint (les
// champs sont `required`), mappé par défense.
const VALIDATION_KEY: Record<PasswordValidationError, string> = {
  empty: "auth.password.errors.generic",
  same: "auth.password.errors.sameAsCurrent",
  mismatch: "auth.password.errors.mismatch",
};

export function AccountDrawer({
  open,
  email,
  onClose,
}: {
  open: boolean;
  email: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { online, pushToast } = useOffline();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Vide les champs à chaque changement d'état ouverture/fermeture : aucun mot
  // de passe ne subsiste en mémoire après fermeture.
  useEffect(() => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setError(null);
    setSaving(false);
  }, [open]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const invalid = validatePasswordChange({ current, next, confirm });
    if (invalid) {
      setError(t(VALIDATION_KEY[invalid]));
      return;
    }
    setSaving(true);
    setError(null);
    // changePassword ne rejette jamais → setSaving(false) s'exécute toujours.
    const res = await changePassword(supabase, { email, current, next });
    setSaving(false);
    if (!res.ok) {
      // update_failed reste générique côté poseurs : impossible d'afficher le
      // message brut Supabase (anglais) dans une app multilingue. Le hint
      // « 6 caractères minimum » sous le champ donne la raison la plus courante.
      setError(
        t(
          res.code === "wrong_current"
            ? "auth.password.errors.wrongCurrent"
            : "auth.password.errors.generic",
        ),
      );
      return;
    }
    pushToast({ kind: "success", message: t("auth.password.success") });
    onClose();
  }

  const inputClass =
    "mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring/40";

  return (
    <Drawer
      open={open}
      onClose={saving ? () => {} : onClose}
      title={t("auth.password.title")}
    >
      {!online ? (
        // Le changement de mot de passe exige le réseau (2 appels Supabase Auth).
        // On ne met rien en file offline : un changement de credential ne doit
        // pas être différé/rejoué.
        <p className="text-sm text-muted-foreground">{t("auth.password.offline")}</p>
      ) : (
        <form onSubmit={submit} className="flex h-full flex-col gap-4">
          <label className="block">
            <span className="text-sm text-muted-foreground">
              {t("auth.password.current")}
            </span>
            <input
              autoFocus
              required
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className={inputClass}
            />
          </label>
          <label className="block">
            <span className="text-sm text-muted-foreground">
              {t("auth.password.new")}
            </span>
            <input
              required
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className={inputClass}
            />
            <span className="mt-1 block text-xs text-muted-foreground">
              {t("auth.password.hint")}
            </span>
          </label>
          <label className="block">
            <span className="text-sm text-muted-foreground">
              {t("auth.password.confirm")}
            </span>
            <input
              required
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={inputClass}
            />
          </label>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="mt-auto flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
              disabled={saving}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" className="flex-1" disabled={saving}>
              {saving ? t("auth.password.saving") : t("auth.password.save")}
            </Button>
          </div>
        </form>
      )}
    </Drawer>
  );
}
